import {
  isExamQuizType,
  parseQuizYaml,
  resolveDriveAssets,
} from "../../../components/Forms/GameQuizForm/yamlImport";
import {
  examCourseErrors,
  examCourseFromCheck,
} from "../../../components/Forms/GameQuizForm/examCourseLink";
import {
  examClaveErrors,
  examCourseIds,
} from "../../../components/Forms/GameQuizForm/examEvaluationLookup";
import type { YamlImportResult } from "../../../components/Forms/GameQuizForm/yamlImport";
import { getIngestionJob } from "../../../services/content-ingestion/content-ingestion.service";
import {
  CHANGED_IN_PLATFORM,
  DOUBLE_QUIZ,
  STRUCTURE_MISMATCH,
} from "../../../services/content-ingestion/content-ingestion.types";
import type {
  IngestErrorResponse,
  IngestionJob,
  IngestionResult,
} from "../../../services/content-ingestion/content-ingestion.types";
import type { GameTagDefinition } from "../../../services/gameTags/gameTags.types";
import { serverMessage } from "../../../utils/serverMessage";
import {
  checkDoc,
  getExamEvaluations,
  getUploadCheck,
  getYamlFile,
  patchHubRow,
  startHubIngestion,
  uploadCourseQuiz,
} from "./contentHub.service";
import { changedReasons } from "./ChangedInPlatformModal";
import type {
  ChangedAnswer,
  ChangedPrompt,
  ChangedReason,
  ChangedReasons,
  CourseQuizKind,
  CourseQuizUpload,
  CourseQuizUploadResult,
  CourseReviewStatus,
  CourseStatusChange,
  DocCheckIssue,
  HubRow,
  PartBoxes,
  PartRun,
  RowRun,
  UploadCheck,
  UploadCheckQuiz,
  YamlFileResponse,
} from "./hubTypes";
import { downloadPlatformVersion } from "./platformDownload";
import {
  DOUBLE_QUIZ_MESSAGE,
  QUIZ_BLOCKED_MESSAGE,
  quizKindFor,
} from "./quizRules";
import {
  docMessages,
  docWithoutCursoNotice,
  quizYamlMessages,
} from "./rowChecks";
import { statusOf } from "./httpStatus";
import {
  asNotice,
  deriveRowState,
  isReadyRow,
  oneLine,
  prefixed,
  rowBlockers,
} from "./rowState";
import type { PartState, ReadyRow, RowState } from "./rowState";

export type RunMode = "validate" | "upload";

export interface RunOptions {
  mode: RunMode;
  rows: HubRow[];
  // Where the rows were read from: the tracker write-back goes to the same place.
  source: { spreadsheet: string; sheet: string };
  tagDefs: GameTagDefinition[];
  onUpdate: (rowNumber: number, patch: RowRun) => void;
  // Checked before each row starts; rows already in flight always finish.
  shouldStop: () => boolean;
  // Asked before uploading a part over edits made in the platform since its
  // last upload, one question at a time while the other rows go on. Without
  // it, nothing is uploaded over them.
  confirmChanged?: (prompt: ChangedPrompt) => Promise<ChangedAnswer>;
  // SCRUM-1581: the quizzes the person agreed, before the run, to replace over
  // the homework and attempts that reach them. Every other quiz is sent for the
  // platform to refuse if any reach it.
  confirmedQuizIds?: ReadonlySet<number>;
  // «Volver a subir lo ya subido»: parts already uploaded go up again, in place.
  reupload?: boolean;
  pollIntervalMs?: number;
  maxDocsInFlight?: number;
  // So tests never wait: how a run sleeps, the jitter added to each retry and
  // the clock the ingestion ceiling reads.
  sleep?: (ms: number) => Promise<void>;
  jitterMs?: () => number;
  now?: () => number;
}

export interface RunResult {
  // Set when the run stopped itself because the tracker could not be written.
  fatal: string | null;
}

const POLL_INTERVAL_MS = 10000;
const MAX_DOCS_IN_FLIGHT = 3;
const MAX_FAILED_POLLS = 3;
// How long a run waits for a job it started. Past it the slot is freed: a later
// run sees the job as still running and starts no duplicate.
const POLL_CEILING_MS = 30 * 60 * 1000;
// Google's truncated exponential backoff: 2^n seconds plus up to one second of
// jitter, never more than 64 seconds, for six retries.
const RETRY_DELAYS_MS = [2000, 4000, 8000, 16000, 32000, 64000];
const MAX_BACKOFF_MS = 64000;
const MAX_JITTER_MS = 1000;
const TRANSIENT_STATUSES = [429, 500, 502, 503, 504];

interface RowContext extends RunOptions {
  pollIntervalMs: number;
  sleep: (ms: number) => Promise<void>;
  jitterMs: () => number;
  now: () => number;
  // How many calls of each row are waiting to retry: its note stays up until
  // the last one is done.
  retryWaits: Map<number, number>;
  // Quizzes go in one at a time, whatever the number of rows in flight.
  inQuizLane: <T>(task: () => Promise<T>) => Promise<T>;
  // And so do the questions to the person.
  inPromptLane: <T>(task: () => Promise<T>) => Promise<T>;
}

// What an upload did to the course's review status; null when not known.
interface ReviewTransition {
  before: CourseReviewStatus | null;
  after: CourseReviewStatus | null;
}

// `present`: the row has a link for the part. `done`: its content is on the
// platform, which is what its box in the tracker says.
// `messages` are errors; `notices` are written to the tracker too but change
// nothing: not the part's state, not its box.
interface PartResult {
  present: boolean;
  done: boolean;
  messages: string[];
  notices: string[];
  // Only for a part whose upload wrote something.
  review?: ReviewTransition;
}

// Uploading ticks the box of each part that is done and that the row has a
// link for. Nothing is sent for the others, and checking sends no box at all.
function boxesFor(mode: RunMode, doc: PartResult, quiz: PartResult): PartBoxes {
  const boxes: PartBoxes = {};
  if (mode === "upload" && doc.present && doc.done) boxes.docUploaded = true;
  if (mode === "upload" && quiz.present && quiz.done) boxes.quizUploaded = true;
  return boxes;
}

const defaultSleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

// A rate limit or a server error may pass by itself, and so may a request that
// got no response (network failure, timeout); anything else would fail again.
const isTransient = (error: unknown): boolean => {
  const status = statusOf(error);
  return status === undefined || TRANSIENT_STATUSES.includes(status);
};

const retryNote = (error: unknown, waitMs: number): string => {
  const reason =
    statusOf(error) === 429
      ? "Google limitó las solicitudes"
      : "Falló una solicitud al servidor";
  return `${reason}; reintento en ${Math.ceil(waitMs / 1000)} s.`;
};

async function waitToRetry(
  row: HubRow,
  ctx: RowContext,
  error: unknown,
  waitMs: number,
): Promise<void> {
  const { rowNumber } = row;
  ctx.retryWaits.set(rowNumber, (ctx.retryWaits.get(rowNumber) ?? 0) + 1);
  ctx.onUpdate(rowNumber, { note: retryNote(error, waitMs) });
  await ctx.sleep(waitMs);
  const waiting = (ctx.retryWaits.get(rowNumber) ?? 1) - 1;
  ctx.retryWaits.set(rowNumber, waiting);
  if (waiting === 0) ctx.onUpdate(rowNumber, { note: null });
}

// Idempotent calls only: repeating one cannot duplicate anything. Detener does
// not cut a wait short, like the ingestion poll: rows in flight always finish.
async function withRetries<T>(
  row: HubRow,
  ctx: RowContext,
  call: () => Promise<T>,
): Promise<T> {
  for (let retry = 0; ; retry += 1) {
    try {
      return await call();
    } catch (error) {
      if (retry >= RETRY_DELAYS_MS.length || !isTransient(error)) throw error;
      const waitMs = Math.min(
        RETRY_DELAYS_MS[retry] + ctx.jitterMs(),
        MAX_BACKOFF_MS,
      );
      await waitToRetry(row, ctx, error, waitMs);
    }
  }
}

function createSerialLane() {
  let tail: Promise<unknown> = Promise.resolve();
  return <T>(task: () => Promise<T>): Promise<T> => {
    const result = tail.then(task);
    tail = result.catch(() => undefined);
    return result;
  };
}

const mark = (
  ctx: RowContext,
  row: HubRow,
  part: "doc" | "quiz",
  run: PartRun,
) => ctx.onUpdate(row.rowNumber, part === "doc" ? { doc: run } : { quiz: run });

const outcome = (ctx: RowContext, row: HubRow, patch: RowRun) =>
  ctx.onUpdate(row.rowNumber, patch);

const unexpectedMessage = (error: unknown) =>
  `Error inesperado: ${error instanceof Error ? error.message : String(error)}`;

// Parts that need no work: absent, already done, still being ingested, or
// blocked by what the course already holds. Null means "pending", and a
// pending part always has its document or YAML file id.
function settledPart(part: PartState): PartResult | null {
  switch (part.status) {
    case "none":
      return { present: false, done: true, messages: [], notices: [] };
    case "done":
      return { present: true, done: true, messages: [], notices: [] };
    case "running":
      return { present: true, done: false, messages: [], notices: [] };
    case "blocked":
      return {
        present: true,
        done: false,
        messages: part.message ? [part.message] : [],
        notices: [],
      };
    default:
      return null;
  }
}

// SCRUM-1532: what the platform holds for the row's course, read once and
// shared by both parts.
type CourseCheck =
  { ok: true; check: UploadCheck } | { ok: false; message: string };

async function loadCourseCheck(
  row: ReadyRow,
  ctx: RowContext,
): Promise<CourseCheck> {
  try {
    const check = await withRetries(row, ctx, () =>
      getUploadCheck(row.courseId),
    );
    return { ok: true, check };
  } catch (error) {
    return {
      ok: false,
      message: serverMessage(
        error,
        "No se pudo revisar el curso en plataforma.",
      ),
    };
  }
}

// When the part was last uploaded and edited; the OD's also say why it changed.
type ChangedDates = Pick<
  ChangedPrompt,
  "lastUploadAt" | "lastPlatformEditAt" | keyof ChangedReasons
>;

const CHANGED = "el curso cambió en plataforma después de su última subida.";
// The platform still says so after the person chose to upload over it.
const STILL_CHANGED =
  "El curso cambió en plataforma después de su última subida.";
const CHANGED_NOTICE = asNotice(CHANGED);
const CANCELLED_NOTICE = asNotice(`no se subió: ${CHANGED}`);
const DOWNLOADED_NOTICE = asNotice("se descargó la versión de plataforma.");

// SCRUM-1532: what the validation notes about an OD that says why it changed.
const REASON_NOTICES: Record<ChangedReason, string> = {
  PLATFORM_EDITS: "el curso está en «Sincronizada: No».",
  REVIEW_NOTES: "el curso tiene comentarios de revisión sin resolver.",
};

const changedNotice = (reasons: ChangedReason[] | undefined): string =>
  reasons?.length
    ? asNotice(reasons.map((reason) => REASON_NOTICES[reason]).join(" "))
    : CHANGED_NOTICE;

// The OD's CHANGED_IN_PLATFORM 409 also says why it changed.
type ErrorBody = IngestErrorResponse & ChangedReasons;

const errorBody = (error: unknown) =>
  (error as { response?: { data?: ErrorBody } }).response?.data;

const changedDates = (data: ErrorBody | undefined): ChangedDates => ({
  lastUploadAt: data?.lastUploadAt ?? null,
  lastPlatformEditAt: data?.lastPlatformEditAt ?? null,
  ...changedReasons(data),
});

const withNotices = (result: PartResult, notices: string[]): PartResult => ({
  ...result,
  notices: [...notices, ...result.notices],
});

// Resolves to "force" when the person chose to upload over the platform's
// edits; otherwise the part ends cancelled, after downloading the platform's
// version when that was the choice.
async function askChanged(
  row: ReadyRow,
  ctx: RowContext,
  part: "doc" | "quiz",
  check: UploadCheck,
  dates: ChangedDates,
): Promise<"force" | PartResult> {
  const prompt: ChangedPrompt = {
    part,
    clave: check.clave ?? (row.clave.trim() || null),
    title: check.title || row.course.title,
    lastUploadAt: dates.lastUploadAt,
    lastPlatformEditAt: dates.lastPlatformEditAt,
    ...changedReasons(dates),
  };
  const { confirmChanged } = ctx;
  const answer: ChangedAnswer = confirmChanged
    ? await ctx.inPromptLane(() => confirmChanged(prompt))
    : "cancel";
  if (answer === "force") return "force";

  mark(ctx, row, part, "cancelled");
  outcome(
    ctx,
    row,
    part === "doc" ? { docOutcome: "cancelled" } : { quizOutcome: "cancelled" },
  );
  const result: PartResult = {
    present: true,
    done: false,
    messages: [],
    notices: [CANCELLED_NOTICE],
  };
  if (answer === "download") {
    const download = await downloadPlatformVersion(row.courseId);
    if (download.ok) result.notices.push(DOWNLOADED_NOTICE);
    else {
      result.messages.push(
        `No se pudo descargar la versión de plataforma: ${download.error}`,
      );
    }
  }
  return result;
}

interface DocCheck {
  errors: string[];
  notices: string[];
  // The OD's lessons, activities or blocks do not match the platform's.
  structure: boolean;
  check: UploadCheck | null;
}

async function checkDocPart(
  row: ReadyRow,
  ctx: RowContext,
  courseCheck: () => Promise<CourseCheck>,
): Promise<DocCheck> {
  let issues: DocCheckIssue[] = [];
  let courseIdFromTracker = false;
  let mismatch: string | null = null;
  const failure: string[] = [];
  try {
    const result = await withRetries(row, ctx, () =>
      checkDoc({
        documentId: row.documentId!,
        courseId: row.courseId,
        courseTitle: row.courseTitle,
      }),
    );
    issues = result.issues;
    courseIdFromTracker = result.courseIdFromTracker;
    mismatch = result.structure?.mismatch ?? null;
  } catch (error) {
    failure.push(serverMessage(error, "No se pudo validar el documento."));
  }
  const course = await courseCheck();
  if (!course.ok) failure.push(course.message);
  return {
    errors: [
      ...docMessages(issues),
      ...(mismatch ? [mismatch] : []),
      ...failure,
    ],
    notices: courseIdFromTracker ? [docWithoutCursoNotice(row.courseId)] : [],
    structure: mismatch !== null,
    check: course.ok ? course.check : null,
  };
}

const startFailureMessage = (error: unknown): string => {
  const errors = errorBody(error)?.errors;
  if (errors?.length) return errors.join("; ");
  return serverMessage(error, "No se pudo iniciar la subida del documento.");
};

type IngestOutcome =
  | { kind: "completed"; result: IngestionResult | null }
  | { kind: "failed"; message: string }
  | { kind: "structure"; message: string }
  | { kind: "changed"; dates: ChangedDates };

function startFailure(error: unknown): IngestOutcome {
  const data = errorBody(error);
  const status = statusOf(error);
  if (status === 422 && data?.error === STRUCTURE_MISMATCH) {
    return { kind: "structure", message: startFailureMessage(error) };
  }
  if (status === 409 && data?.error === CHANGED_IN_PLATFORM) {
    return { kind: "changed", dates: changedDates(data) };
  }
  return { kind: "failed", message: startFailureMessage(error) };
}

async function ingestDoc(
  row: ReadyRow,
  ctx: RowContext,
  flags: { replace: boolean; force: boolean },
): Promise<IngestOutcome> {
  let jobId: number;
  try {
    ({ jobId } = await startHubIngestion({
      documentId: row.documentId!,
      courseId: row.courseId,
      ...(flags.replace && { mode: "replace" as const }),
      ...(flags.force && { force: true }),
    }));
  } catch (error) {
    return startFailure(error);
  }

  const startedAt = ctx.now();
  let failedPolls = 0;
  for (;;) {
    if (ctx.now() - startedAt >= POLL_CEILING_MS) {
      return {
        kind: "failed",
        message: `El trabajo de subida ${jobId} sigue en curso después de ${POLL_CEILING_MS / 60000} minutos; la próxima pasada revisa cómo terminó.`,
      };
    }
    await ctx.sleep(ctx.pollIntervalMs);
    let job: IngestionJob;
    try {
      job = await getIngestionJob(jobId);
    } catch (error) {
      failedPolls += 1;
      if (failedPolls >= MAX_FAILED_POLLS) {
        return {
          kind: "failed",
          message: serverMessage(
            error,
            "Se perdió la conexión con el servidor mientras se revisaba el avance de la subida.",
          ),
        };
      }
      continue;
    }
    failedPolls = 0;
    ctx.onUpdate(row.rowNumber, { docProgress: job.progress });
    if (job.status === "COMPLETED") {
      return { kind: "completed", result: job.result ?? null };
    }
    if (job.status === "FAILED") {
      return {
        kind: "failed",
        message: job.error_message ?? "La subida del documento falló.",
      };
    }
  }
}

// The job says what it did to the review status; a job that does not is read
// from the course again.
async function reviewAfterDoc(
  row: ReadyRow,
  ctx: RowContext,
  check: UploadCheck,
  result: IngestionResult | null,
): Promise<ReviewTransition> {
  const before = result?.reviewStatusBefore ?? check.reviewStatus;
  if (result?.reviewStatusAfter) {
    return { before, after: result.reviewStatusAfter };
  }
  try {
    const fresh = await withRetries(row, ctx, () =>
      getUploadCheck(row.courseId),
    );
    return { before, after: fresh.reviewStatus };
  } catch {
    return { before, after: null };
  }
}

async function uploadDoc(
  row: ReadyRow,
  ctx: RowContext,
  check: UploadCheck,
  flags: { replace: boolean; force: boolean },
  notices: string[],
): Promise<PartResult> {
  mark(ctx, row, "doc", "uploading");
  let ingested = await ingestDoc(row, ctx, flags);
  // The platform was edited after the check: the same question, then.
  if (ingested.kind === "changed" && !flags.force) {
    const answer = await askChanged(row, ctx, "doc", check, ingested.dates);
    if (answer !== "force") return withNotices(answer, notices);
    mark(ctx, row, "doc", "uploading");
    ingested = await ingestDoc(row, ctx, { ...flags, force: true });
  }

  if (ingested.kind === "completed") {
    mark(ctx, row, "doc", "done");
    outcome(ctx, row, { docOutcome: flags.replace ? "replaced" : "created" });
    const review = await reviewAfterDoc(row, ctx, check, ingested.result);
    return { present: true, done: true, messages: [], notices, review };
  }
  mark(ctx, row, "doc", "error");
  if (ingested.kind === "structure") {
    outcome(ctx, row, { docOutcome: "structure" });
  }
  const message =
    ingested.kind === "changed" ? STILL_CHANGED : ingested.message;
  return { present: true, done: false, messages: [message], notices };
}

async function runDocPart(
  row: ReadyRow,
  state: RowState,
  ctx: RowContext,
  courseCheck: () => Promise<CourseCheck>,
): Promise<PartResult> {
  const settled = settledPart(state.doc);
  if (settled) return settled;

  mark(ctx, row, "doc", "checking");
  const { errors, notices, structure, check } = await checkDocPart(
    row,
    ctx,
    courseCheck,
  );
  if (errors.length > 0 || check === null) {
    mark(ctx, row, "doc", "error");
    if (structure) outcome(ctx, row, { docOutcome: "structure" });
    return { present: true, done: false, messages: errors, notices };
  }
  // A course with lessons gets the OD in place of what it holds, same IDs.
  const replace = check.lessonCount > 0;
  const changed = replace && check.od.changedInPlatform;
  if (ctx.mode === "validate") {
    mark(ctx, row, "doc", "valid");
    return {
      present: true,
      done: false,
      messages: [],
      notices: [
        ...notices,
        ...(changed ? [changedNotice(check.od.reasons)] : []),
      ],
    };
  }

  let force = false;
  if (changed) {
    const answer = await askChanged(row, ctx, "doc", check, check.od);
    if (answer !== "force") return withNotices(answer, notices);
    force = true;
  }
  return uploadDoc(row, ctx, check, { replace, force }, notices);
}

interface ReadyQuiz {
  parsed: YamlImportResult;
  kind: CourseQuizKind;
  check: UploadCheck;
  studentRecords: "ask" | "overwrite";
}

type QuizCheck =
  | (ReadyQuiz & { notices: string[] })
  | { messages: string[]; notices: string[] };

// The quiz a YAML of this kind replaces: the video's for a video YAML, else
// the course's only one (two at once is an error of its own).
const targetQuiz = (
  quizzes: UploadCheckQuiz[],
  kind: CourseQuizKind,
): UploadCheckQuiz | undefined =>
  kind === "video" ? quizzes.find((quiz) => quiz.kind === "video") : quizzes[0];

async function checkQuizPart(
  row: ReadyRow,
  ctx: RowContext,
  courseCheck: () => Promise<CourseCheck>,
): Promise<QuizCheck> {
  const course = await courseCheck();
  if (!course.ok) return { messages: [course.message], notices: [] };
  if (course.check.doubleQuizError) {
    return { messages: [DOUBLE_QUIZ_MESSAGE], notices: [] };
  }
  let file: YamlFileResponse;
  try {
    file = await withRetries(row, ctx, () => getYamlFile(row.yamlFileId!));
  } catch (error) {
    return {
      messages: [serverMessage(error, "No se pudo leer el YAML.")],
      notices: [],
    };
  }
  const kind = quizKindFor(file.name);
  // SCRUM-1581: what students did on it never blocks it; only a quiz confirmed
  // before the run goes over what reaches it.
  const target = targetQuiz(course.check.quizzes, kind);
  const studentRecords =
    target && ctx.confirmedQuizIds?.has(target.gameQuizId)
      ? "overwrite"
      : "ask";
  const parsed = parseQuizYaml(file.text, ctx.tagDefs);
  const { errors, notices } = quizYamlMessages(parsed, row, file.name);
  if (isExamQuizType(parsed.tipo)) {
    errors.push(...(await examUploadErrors(kind, course.check, parsed)));
  }
  return errors.length > 0
    ? { messages: errors, notices }
    : { parsed, kind, check: course.check, studentRecords, notices };
}

const EXAM_VIDEO_MESSAGE = "Un examen no puede ser el quiz de un video.";

// SCRUM-1581: `needs_confirmation`, homework or attempts reached a quiz nobody
// confirmed before the run, and nothing was written.
const UNCONFIRMED_RECORDS_MESSAGE =
  "No se subió: tiene tareas o intentos de alumnos que no se confirmaron al empezar. Vuelve a subir la fila.";

async function examUploadErrors(
  kind: CourseQuizKind,
  check: UploadCheck,
  parsed: YamlImportResult,
): Promise<string[]> {
  if (kind === "video") return [EXAM_VIDEO_MESSAGE];
  const errors = examCourseErrors(examCourseFromCheck(check), parsed.games);
  if (errors.length > 0) return errors;
  const claves = parsed.questionClaves ?? [];
  if (!claves.some(Boolean)) return [];
  try {
    const { courses } = await getExamEvaluations(examCourseIds(parsed.games));
    return examClaveErrors(parsed.games, claves, courses);
  } catch (error) {
    return [
      serverMessage(error, "No se pudieron comprobar las claves del examen."),
    ];
  }
}

const quizContent = (parsed: YamlImportResult): CourseQuizUpload["quiz"] =>
  isExamQuizType(parsed.tipo)
    ? {
        title: parsed.title,
        games: parsed.games,
        kind: "EXAM",
        situations: parsed.situations ?? [],
      }
    : { title: parsed.title, games: parsed.games };

type QuizSent =
  | { kind: "result"; result: CourseQuizUploadResult }
  | { kind: "failed"; message: string }
  | { kind: "changed"; dates: ChangedDates };

// One call replaces the course's quiz in place, or creates it with its
// resource; the platform never leaves half of it behind.
async function sendQuiz(
  row: ReadyRow,
  quiz: ReadyQuiz,
  force: boolean,
): Promise<QuizSent> {
  try {
    const result = await uploadCourseQuiz(row.courseId, {
      kind: quiz.kind,
      quiz: quizContent(quiz.parsed),
      ...(force && { force: true }),
      student_records: quiz.studentRecords,
    });
    return { kind: "result", result };
  } catch (error) {
    const data = errorBody(error);
    if (statusOf(error) === 409 && data?.error === DOUBLE_QUIZ) {
      return { kind: "failed", message: DOUBLE_QUIZ_MESSAGE };
    }
    if (statusOf(error) === 409 && data?.error === CHANGED_IN_PLATFORM) {
      return { kind: "changed", dates: changedDates(data) };
    }
    return {
      kind: "failed",
      message: serverMessage(error, "No se pudo subir el quiz."),
    };
  }
}

// The SCRUM-1153 import of the media goes first; nobody reviews a quiz that
// kept a Drive link, as the form lets a person do.
async function importQuiz(
  row: ReadyRow,
  quiz: ReadyQuiz,
  force: boolean,
): Promise<QuizSent> {
  const { failed } = await resolveDriveAssets(
    isExamQuizType(quiz.parsed.tipo)
      ? { games: quiz.parsed.games, situations: quiz.parsed.situations }
      : quiz.parsed.games,
  );
  if (failed.length > 0) {
    return {
      kind: "failed",
      message: `No se pudieron importar estos archivos de Drive (revisa que estén compartidos con la cuenta de servicio): ${failed.join(", ")}`,
    };
  }
  return sendQuiz(row, quiz, force);
}

async function uploadQuiz(
  row: ReadyRow,
  quiz: ReadyQuiz,
  ctx: RowContext,
  force: boolean,
  notices: string[],
): Promise<PartResult> {
  mark(ctx, row, "quiz", "uploading");
  let sent = await ctx.inQuizLane(() => importQuiz(row, quiz, force));
  if (sent.kind === "changed" && !force) {
    const answer = await askChanged(row, ctx, "quiz", quiz.check, sent.dates);
    if (answer !== "force") return withNotices(answer, notices);
    mark(ctx, row, "quiz", "uploading");
    sent = await ctx.inQuizLane(() => sendQuiz(row, quiz, true));
  }

  if (
    sent.kind === "result" &&
    (sent.result.result === "replaced" || sent.result.result === "created")
  ) {
    const { result, resourceId, reviewStatusBefore, reviewStatusAfter } =
      sent.result;
    mark(ctx, row, "quiz", "done");
    outcome(
      ctx,
      row,
      result === "created"
        ? { quizOutcome: "created", quizResourceId: resourceId }
        : { quizOutcome: "replaced" },
    );
    return {
      present: true,
      done: true,
      messages: [],
      notices,
      review: { before: reviewStatusBefore, after: reviewStatusAfter },
    };
  }
  mark(ctx, row, "quiz", "error");
  // `blocked` only comes from an API that does not know `student_records`.
  const blocked = sent.kind === "result" && sent.result.result === "blocked";
  if (blocked) outcome(ctx, row, { quizOutcome: "blocked" });
  const message =
    sent.kind === "failed"
      ? sent.message
      : sent.kind === "changed"
        ? STILL_CHANGED
        : blocked
          ? QUIZ_BLOCKED_MESSAGE
          : UNCONFIRMED_RECORDS_MESSAGE;
  return { present: true, done: false, messages: [message], notices };
}

async function runQuizPart(
  row: ReadyRow,
  state: RowState,
  ctx: RowContext,
  courseCheck: () => Promise<CourseCheck>,
): Promise<PartResult> {
  const settled = settledPart(state.quiz);
  if (settled) return settled;

  mark(ctx, row, "quiz", "checking");
  const check = await checkQuizPart(row, ctx, courseCheck);
  if (!("parsed" in check)) {
    mark(ctx, row, "quiz", "error");
    return {
      present: true,
      done: false,
      messages: check.messages,
      notices: check.notices,
    };
  }
  const changed = check.check.quiz.changedInPlatform;
  if (ctx.mode === "validate") {
    mark(ctx, row, "quiz", "valid");
    return {
      present: true,
      done: false,
      messages: [],
      notices: [...check.notices, ...(changed ? [CHANGED_NOTICE] : [])],
    };
  }

  let force = false;
  if (changed) {
    const answer = await askChanged(
      row,
      ctx,
      "quiz",
      check.check,
      check.check.quiz,
    );
    if (answer !== "force") return withNotices(answer, check.notices);
    force = true;
  }
  return uploadQuiz(row, check, ctx, force, check.notices);
}

// Whether the uploads took the course from Revisada to Finalizada, left it in
// Por revisar, or changed nothing. Unknown when its check could not be read.
function courseStatusOf(
  course: CourseCheck,
  reviews: Array<ReviewTransition | undefined>,
): CourseStatusChange | undefined {
  if (!course.ok) return undefined;
  const transitions = reviews.filter(
    (review): review is ReviewTransition => review !== undefined,
  );
  if (
    transitions.some(
      ({ before, after }) => before === "REVISADA" && after === "FINALIZADA",
    )
  ) {
    return "finalized";
  }
  const afters = transitions
    .map(({ after }) => after)
    .filter((after): after is CourseReviewStatus => after !== null);
  const current = afters.length > 0 ? afters : [course.check.reviewStatus];
  return current.every((status) => status === "POR_REVISAR")
    ? "stillPorRevisar"
    : "unchanged";
}

function writeFailureReason(error: unknown): string {
  if (statusOf(error) === 409) {
    return "El tracker cambió mientras se procesaba (las filas se movieron). Carga las filas de nuevo.";
  }
  return serverMessage(error, "No se pudo escribir en el tracker.");
}

// Resolves to a message when the tracker could not be written, which stops
// the run: content uploaded without a record is only recoverable by hand.
async function writeBack(
  row: HubRow,
  ctx: RowContext,
  text: { errors: string[]; notices: string[] },
  boxes: PartBoxes,
): Promise<string | null> {
  const errors = text.errors.map(oneLine);
  const notices = text.notices.map(oneLine);
  const lines = [...errors, ...notices];
  try {
    await withRetries(row, ctx, () =>
      patchHubRow(row.rowNumber, {
        ...ctx.source,
        expectedCourseId: row.courseId,
        ...boxes,
        error: lines.length > 0 ? lines.join("\n") : null,
      }),
    );
    return null;
  } catch (error) {
    return `Se detuvo la carga: no se pudo escribir la fila ${row.rowNumber} en el tracker. ${writeFailureReason(error)}`;
  } finally {
    ctx.onUpdate(row.rowNumber, { messages: errors, notices });
  }
}

async function processRow(
  row: HubRow,
  ctx: RowContext,
): Promise<string | null> {
  // A row-level problem is what the tracker must say; nothing else is touched,
  // and no box either.
  if (!isReadyRow(row)) {
    return writeBack(row, ctx, { errors: rowBlockers(row), notices: [] }, {});
  }

  const state = deriveRowState(row, { reupload: ctx.reupload });
  // Read only when a part needs it, and then once for both.
  const shared: { check: Promise<CourseCheck> | null } = { check: null };
  const courseCheck = () => {
    shared.check = shared.check ?? loadCourseCheck(row, ctx);
    return shared.check;
  };
  // One bad part must not take the whole run down with it.
  const guarded = async (
    part: "doc" | "quiz",
    work: () => Promise<PartResult>,
  ): Promise<PartResult> => {
    try {
      return await work();
    } catch (error) {
      mark(ctx, row, part, "error");
      return {
        present: true,
        done: false,
        messages: [unexpectedMessage(error)],
        notices: [],
      };
    }
  };
  const [doc, quiz] = await Promise.all([
    guarded("doc", () => runDocPart(row, state, ctx, courseCheck)),
    guarded("quiz", () => runQuizPart(row, state, ctx, courseCheck)),
  ]);
  if (shared.check) {
    const courseStatus = courseStatusOf(await shared.check, [
      doc.review,
      quiz.review,
    ]);
    if (courseStatus) ctx.onUpdate(row.rowNumber, { courseStatus });
  }

  const text = {
    errors: [
      ...prefixed("Doc", doc.messages),
      ...prefixed("Quiz", quiz.messages),
    ],
    notices: [
      ...prefixed("Doc", doc.notices),
      ...prefixed("Quiz", quiz.notices),
    ],
  };
  return writeBack(row, ctx, text, boxesFor(ctx.mode, doc, quiz));
}

export async function runHub(options: RunOptions): Promise<RunResult> {
  const ctx: RowContext = {
    ...options,
    pollIntervalMs: options.pollIntervalMs ?? POLL_INTERVAL_MS,
    sleep: options.sleep ?? defaultSleep,
    jitterMs: options.jitterMs ?? (() => Math.random() * MAX_JITTER_MS),
    now: options.now ?? Date.now,
    retryWaits: new Map(),
    inQuizLane: createSerialLane(),
    inPromptLane: createSerialLane(),
  };
  const queue = [...options.rows];
  let fatal: string | null = null;

  const worker = async () => {
    while (fatal === null && !options.shouldStop()) {
      const row = queue.shift();
      if (!row) return;
      const failure = await processRow(row, ctx);
      fatal = fatal ?? failure;
    }
  };

  // Validation goes row by row; uploads keep up to three rows (so three
  // documents) in flight.
  const workers =
    options.mode === "upload"
      ? (options.maxDocsInFlight ?? MAX_DOCS_IN_FLIGHT)
      : 1;
  await Promise.all(Array.from({ length: workers }, worker));
  return { fatal };
}
